import { PeriodSelector } from "@/components/school-head/PeriodSelector";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePeriodRange } from "@/hooks/useSchoolHeadData";

export default function SchoolHeadMonitoring() {
  const { user } = useAuth();
  const { start, end } = usePeriodRange();

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["sh-monitoring-profiles", user?.schoolId],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("special_monitoring_profiles")
        .select("id,is_active,notes,student_id,students(surname,other_names,class)")
        .eq("school_id", user!.schoolId);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const { data: readings = [] } = useQuery({
    queryKey: ["sh-monitoring-readings", user?.schoolId, start, end],
    enabled: !!user?.schoolId && !!start && !!end,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("special_monitoring_readings")
        .select("id,profile_id,recorded_at")
        .gte("recorded_at", start!)
        .lte("recorded_at", end!);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const readingByProfile = readings.reduce<Record<string, number>>((acc, r) => {
    acc[r.profile_id] = (acc[r.profile_id] || 0) + 1;
    return acc;
  }, {});

  const active = profiles.filter((p) => p.is_active);
  const inactive = profiles.filter((p) => !p.is_active);

  return (
    <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Special Monitoring</h1>
          <p className="text-sm text-muted-foreground">
            Students on active monitoring and reading counts for the selected period.
          </p>
        </div>

        <PeriodSelector />

        <div className="grid gap-3 sm:grid-cols-3">
          <Tile label="Active profiles" value={active.length} />
          <Tile label="Inactive / closed" value={inactive.length} />
          <Tile label="Readings in period" value={readings.length} />
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Class</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Readings in period</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={4}>Loading…</td></tr>
                  ) : profiles.length === 0 ? (
                    <tr><td className="px-4 py-6 text-muted-foreground" colSpan={4}>No monitoring profiles.</td></tr>
                  ) : (
                    profiles.map((p) => (
                      <tr key={p.id} className="border-t">
                        <td className="px-4 py-2 font-medium">
                          {p.students ? `${p.students.surname} ${p.students.other_names || ""}` : "—"}
                        </td>
                        <td className="px-4 py-2">{p.students?.class || "—"}</td>
                        <td className="px-4 py-2">{p.is_active ? "Active" : "Closed"}</td>
                        <td className="px-4 py-2">{readingByProfile[p.id] || 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
